import { useEffect, type Dispatch } from "react";
import { toast } from "sonner";
import type { AudienceSelectionRef } from "@digital-twins/shared-types";
import i18n from "@/i18n";
import { populationsApi } from "@/lib/api";
import { draftAudienceKey } from "@/features/studies/questionnaire/utils/draftAudience";
import type { CreationAction } from "../state/creationState";

/**
 * Sizes the reviewed audience's filter: the one filter preview Review's cap,
 * limits and price read (`reviewedPoolOf`), live and after a reload alike.
 * Asked once per audience (`draftAudienceKey`, which a sample-size re-pin
 * leaves alone). When it fails the count stays unknown: Review caps on the
 * whole pool and the launch's own pool check stays the authority.
 */
export function useReviewedFilteredCount(
  reviewedAudience: AudienceSelectionRef | null,
  dispatch: Dispatch<CreationAction>,
): void {
  const populationId = reviewedAudience?.populationId ?? null;
  const filter = reviewedAudience?.populationFilter ?? null;
  const audienceKey =
    reviewedAudience && populationId && filter
      ? draftAudienceKey(reviewedAudience)
      : null;

  useEffect(() => {
    if (!audienceKey || !populationId || !filter) {
      return;
    }
    let cancelled = false;
    populationsApi.previewFilter(populationId, filter).then(
      ({ matchingCount }) => {
        if (!cancelled) {
          dispatch({
            type: "SET_REVIEWED_FILTERED_COUNT",
            audienceKey,
            count: matchingCount,
          });
        }
      },
      (error: unknown) => {
        if (cancelled) return;
        console.error("Failed to size the reviewed audience filter:", error);
        toast.error(i18n.t("creation.filteredCountUnavailable", { ns: "app" }));
      },
    );
    return () => {
      cancelled = true;
    };
    // The key names the audience; the filter object's identity does not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceKey, dispatch]);
}
